import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { ConsultantClientContext, ConsultationThread, DietTypes, GuidedClient, GuidedClientAccess, User } from '../types';
import { getTranslation } from '../utils/translations';
import { getUserDisplayName } from '../utils/userDisplay';
import { EMPTY_DIET_TYPES, normalizeDietTypes, DIET_TYPE_CONFIG } from '../utils/dietTypes';
import { normalizeKnownAllergies, ALLERGY_CONFIG, type KnownAllergyId } from '../utils/allergies';
import KnownAllergiesSelector from './KnownAllergiesSelector';
import DietTypesSelector from './DietTypesSelector';
import {
    refreshGuidedClientAccess,
    setGuidedClientAccessActive,
    updateGuidedClientProfile,
} from '../services/guidedClientService';
import CopyButton from './CopyButton';
import ConsultationThreadPanel from './ConsultationThreadPanel';
import XIcon from './icons/XIcon';
import SearchField from './SearchField';
import ChevronDownIcon from './icons/ChevronDownIcon';
import {
    buildConsultationThreadId,
    subscribeConsultantConsultationThreads,
    threadHasUnreadForConsultant,
} from '../utils/consultationThreads';
import AvatarImage from './AvatarImage';
import { getInitials } from '../hooks/useConsultantBannerData';

interface GuidedClientManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUser: User;
    allUsers?: User[];
    onOpenClientHousehold: (context: ConsultantClientContext) => void;
    onCreateClient?: () => void;
}

type AccessWithClient = GuidedClientAccess & { client?: GuidedClient };
type ClientStatusFilter = 'active' | 'inactive';

interface CollapsibleTagSectionProps {
    title: string;
    count: number;
    expanded: boolean;
    onToggle: () => void;
    summary: React.ReactNode;
    children: React.ReactNode;
}

const CollapsibleTagSection: React.FC<CollapsibleTagSectionProps> = ({
    title,
    count,
    expanded,
    onToggle,
    summary,
    children,
}) => (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-start"
        >
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                {title}
                {count > 0 && (
                    <span className="ms-1.5 text-brand-sage normal-case tracking-normal font-bold">
                        ({count})
                    </span>
                )}
            </span>
            <ChevronDownIcon className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {!expanded && count > 0 && (
            <div className="px-3 pb-3 pt-1 flex flex-wrap gap-1.5 bg-slate-50 dark:bg-slate-800/50">
                {summary}
            </div>
        )}
        {expanded && (
            <div className="p-3 border-t border-slate-200 dark:border-slate-700">
                {children}
            </div>
        )}
    </div>
);

const GuidedClientManagementModal: React.FC<GuidedClientManagementModalProps> = ({
    isOpen,
    onClose,
    currentUser,
    allUsers = [],
    onOpenClientHousehold,
    onCreateClient,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) =>
        getTranslation(currentUser.language || 'en', key);

    const isSuperuserView = currentUser.role === 'superuser';

    const [accessRows, setAccessRows] = useState<GuidedClientAccess[]>([]);
    const [clients, setClients] = useState<Record<string, GuidedClient>>({});
    const [threadsById, setThreadsById] = useState<Record<string, ConsultationThread>>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<ClientStatusFilter>('active');
    const [notificationsOnly, setNotificationsOnly] = useState(false);
    const [selectedAccessId, setSelectedAccessId] = useState<string | null>(null);
    const [accessUrl, setAccessUrl] = useState('');
    const [loadingAction, setLoadingAction] = useState(false);
    const [error, setError] = useState('');
    const [editAllergies, setEditAllergies] = useState<KnownAllergyId[]>([]);
    const [editDietTypes, setEditDietTypes] = useState<DietTypes>({ ...EMPTY_DIET_TYPES });
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [allergiesExpanded, setAllergiesExpanded] = useState(false);
    const [dietTypesExpanded, setDietTypesExpanded] = useState(false);
    const [linkedUsersByUid, setLinkedUsersByUid] = useState<Record<string, Pick<User, 'avatarUrl'>>>({});

    const usersById = useMemo(() => {
        const map: Record<string, User> = {};
        allUsers.forEach((entry) => {
            map[entry.uid] = entry;
        });
        return map;
    }, [allUsers]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedAccessId(null);
            setSearchQuery('');
            setStatusFilter('active');
            setNotificationsOnly(false);
            setAccessUrl('');
            setError('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !currentUser.uid) return;

        const qAccess = isSuperuserView
            ? collection(db, 'guidedClientAccess')
            : query(
                collection(db, 'guidedClientAccess'),
                where('consultantId', '==', currentUser.uid),
            );

        const unsubAccess = onSnapshot(qAccess, (snap) => {
            setAccessRows(snap.docs.map((d) => ({ ...(d.data() as Omit<GuidedClientAccess, 'id'>), id: d.id })));
        });

        const unsubClients = onSnapshot(collection(db, 'guidedClients'), (snap) => {
            const map: Record<string, GuidedClient> = {};
            snap.docs.forEach((d) => {
                map[d.id] = { ...(d.data() as Omit<GuidedClient, 'id'>), id: d.id };
            });
            setClients(map);
        });

        let unsubThreads: (() => void) | undefined;
        if (isSuperuserView) {
            unsubThreads = onSnapshot(collection(db, 'consultationThreads'), (snap) => {
                const map: Record<string, ConsultationThread> = {};
                snap.docs.forEach((d) => {
                    map[d.id] = { ...(d.data() as Omit<ConsultationThread, 'id'>), id: d.id };
                });
                setThreadsById(map);
            });
        } else {
            unsubThreads = subscribeConsultantConsultationThreads(currentUser.uid, (threads) => {
                const map: Record<string, ConsultationThread> = {};
                threads.forEach((thread) => {
                    map[thread.id] = thread;
                });
                setThreadsById(map);
            });
        }

        return () => {
            unsubAccess();
            unsubClients();
            unsubThreads?.();
        };
    }, [isOpen, currentUser.uid, isSuperuserView]);

    useEffect(() => {
        if (!isOpen) {
            setLinkedUsersByUid({});
            return;
        }

        const uids = [
            ...new Set(
                Object.values(clients)
                    .map((client) => client.userUid)
                    .filter((uid): uid is string => Boolean(uid)),
            ),
        ];

        if (uids.length === 0) return;

        const unsubs = uids.map((uid) =>
            onSnapshot(doc(db, 'users', uid), (snap) => {
                if (!snap.exists()) return;
                const data = snap.data() as User;
                setLinkedUsersByUid((prev) => ({
                    ...prev,
                    [uid]: { avatarUrl: data.avatarUrl },
                }));
            }),
        );

        return () => unsubs.forEach((unsub) => unsub());
    }, [isOpen, clients]);

    const getClientAvatarUrl = (client?: GuidedClient): string | null | undefined => {
        if (!client?.userUid) return undefined;
        return usersById[client.userUid]?.avatarUrl ?? linkedUsersByUid[client.userUid]?.avatarUrl;
    };

    const getThreadForRow = (row: AccessWithClient): ConsultationThread | undefined =>
        threadsById[buildConsultationThreadId(row.clientId, row.consultantId)];

    const hasUnread = (row: AccessWithClient): boolean => {
        const thread = getThreadForRow(row);
        return thread ? threadHasUnreadForConsultant(thread) : false;
    };

    const rows: AccessWithClient[] = useMemo(() => {
        return accessRows
            .map((row) => ({ ...row, client: clients[row.clientId] }))
            .sort((a, b) => {
                const aUnread = hasUnread(a) ? 1 : 0;
                const bUnread = hasUnread(b) ? 1 : 0;
                if (aUnread !== bUnread) return bUnread - aUnread;

                const aLabel = a.client
                    ? [a.client.firstName, a.client.lastName].filter(Boolean).join(' ').trim() || a.client.email
                    : a.clientId;
                const bLabel = b.client
                    ? [b.client.firstName, b.client.lastName].filter(Boolean).join(' ').trim() || b.client.email
                    : b.clientId;
                return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
            });
    }, [accessRows, clients, threadsById]);

    const unreadCount = useMemo(
        () => rows.filter((row) => row.isActive && hasUnread(row)).length,
        [rows, threadsById],
    );

    const filteredRows = useMemo(() => {
        let result = rows;

        result = result.filter((row) => (statusFilter === 'active' ? row.isActive : !row.isActive));

        if (notificationsOnly) {
            result = result.filter((row) => hasUnread(row));
        }

        const q = searchQuery.trim().toLowerCase();
        if (q) {
            result = result.filter((row) => {
                const c = row.client;
                const consultant = usersById[row.consultantId];
                const consultantLabel = consultant ? getUserDisplayName(consultant).toLowerCase() : '';
                if (!c) return row.clientId.toLowerCase().includes(q) || consultantLabel.includes(q);
                return (
                    c.email.toLowerCase().includes(q)
                    || (c.firstName || '').toLowerCase().includes(q)
                    || (c.lastName || '').toLowerCase().includes(q)
                    || (c.nickname || '').toLowerCase().includes(q)
                    || consultantLabel.includes(q)
                );
            });
        }

        return result;
    }, [rows, statusFilter, notificationsOnly, searchQuery, usersById, threadsById]);

    const selected = filteredRows.find((r) => r.id === selectedAccessId)
        || rows.find((r) => r.id === selectedAccessId);

    useEffect(() => {
        if (!selected?.client) return;
        setEditFirstName(selected.client.firstName || '');
        setEditLastName(selected.client.lastName || '');
        setAllergiesExpanded(false);
        setDietTypesExpanded(false);
        setEditAllergies(normalizeKnownAllergies(selected.client.knownAllergies));
        setEditDietTypes(normalizeDietTypes(selected.client.userDietTypes));
        setAccessUrl('');
        setError('');
    }, [selectedAccessId, selected?.client?.id]);

    if (!isOpen) return null;

    const getConsultantLabel = (consultantId: string): string => {
        const consultant = usersById[consultantId];
        return consultant ? getUserDisplayName(consultant) : consultantId;
    };

    const getClientLabel = (row: AccessWithClient): string => {
        const c = row.client;
        if (!c) return row.clientId;
        return [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email;
    };

    const handleRefreshLink = async () => {
        if (!selected) return;
        setLoadingAction(true);
        setError('');
        try {
            const result = await refreshGuidedClientAccess(selected.id);
            setAccessUrl(result.accessUrl);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setLoadingAction(false);
        }
    };

    const handleToggleActive = async () => {
        if (!selected) return;
        setLoadingAction(true);
        setError('');
        try {
            await setGuidedClientAccessActive(selected.id, !selected.isActive);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setLoadingAction(false);
        }
    };

    const handleSaveProfile = async () => {
        if (!selected?.client) return;
        setLoadingAction(true);
        setError('');
        try {
            await updateGuidedClientProfile(selected.clientId, {
                firstName: editFirstName.trim(),
                lastName: editLastName.trim(),
                knownAllergies: editAllergies,
                userDietTypes: editDietTypes,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setLoadingAction(false);
        }
    };

    const handleOpenHousehold = () => {
        if (!selected?.client) return;
        onOpenClientHousehold({
            clientId: selected.clientId,
            accessId: selected.id,
            householdId: selected.householdId,
            clientName: getClientLabel(selected),
        });
        onClose();
    };

    const renderClientDetail = () => {
        if (!selected?.client) {
            return (
                <p className="text-sm text-slate-500 p-6">{t('selectGuidedClient')}</p>
            );
        }

        const clientLabel = getClientLabel(selected);
        const clientInitials = getInitials(clientLabel);

        return (
            <div className="p-4 md:p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
                    <AvatarImage
                        src={getClientAvatarUrl(selected.client)}
                        alt={clientLabel}
                        className="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-slate-900 shadow-sm bg-brand-sage/15 shrink-0"
                        fallback={
                            <div className="w-12 h-12 rounded-full bg-brand-sage/15 text-brand-sage flex items-center justify-center font-black text-sm shrink-0 border-2 border-white dark:border-slate-900">
                                {clientInitials}
                            </div>
                        }
                    />
                    <div className="min-w-0">
                        <p className="font-black text-slate-800 dark:text-slate-100 truncate">{clientLabel}</p>
                        <p className="text-xs text-slate-400 mt-0.5 truncate dir-ltr text-start">{selected.client.email}</p>
                    </div>
                </div>

                {isSuperuserView && (
                    <div>
                        <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">{t('assignedConsultant')}</p>
                        <p className="text-sm font-bold">{getConsultantLabel(selected.consultantId)}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('firstName')}</label>
                        <input
                            value={editFirstName}
                            onChange={(e) => setEditFirstName(e.target.value)}
                            className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('lastName')}</label>
                        <input
                            value={editLastName}
                            onChange={(e) => setEditLastName(e.target.value)}
                            className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
                        />
                    </div>
                </div>

                <CollapsibleTagSection
                    title={t('knownAllergies')}
                    count={editAllergies.length}
                    expanded={allergiesExpanded}
                    onToggle={() => setAllergiesExpanded((prev) => !prev)}
                    summary={editAllergies.map((id) => {
                        const config = ALLERGY_CONFIG.find((entry) => entry.id === id);
                        if (!config) return null;
                        return (
                            <span
                                key={id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                            >
                                <span>{config.emoji}</span>
                                {t(config.labelKey as Parameters<typeof getTranslation>[1])}
                            </span>
                        );
                    })}
                >
                    <KnownAllergiesSelector
                        value={editAllergies}
                        onChange={setEditAllergies}
                        language={currentUser.language}
                        disabled={loadingAction}
                    />
                </CollapsibleTagSection>

                <CollapsibleTagSection
                    title={t('dietLabels')}
                    count={DIET_TYPE_CONFIG.filter(({ key }) => editDietTypes[key]).length}
                    expanded={dietTypesExpanded}
                    onToggle={() => setDietTypesExpanded((prev) => !prev)}
                    summary={DIET_TYPE_CONFIG.filter(({ key }) => editDietTypes[key]).map(({ key, labelKey, emoji }) => (
                        <span
                            key={key}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-brand-sage/10 text-brand-sage"
                        >
                            <span>{emoji}</span>
                            {t(labelKey as Parameters<typeof getTranslation>[1])}
                        </span>
                    ))}
                >
                    <DietTypesSelector
                        value={editDietTypes}
                        onChange={setEditDietTypes}
                        language={currentUser.language}
                        disabled={loadingAction}
                    />
                </CollapsibleTagSection>

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={loadingAction}
                        className="px-4 py-2.5 bg-brand-sage text-white text-sm font-black rounded-lg disabled:opacity-50"
                    >
                        {t('save')}
                    </button>
                    <button
                        type="button"
                        onClick={handleToggleActive}
                        disabled={loadingAction}
                        className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-sm font-black rounded-lg"
                    >
                        {selected.isActive ? t('deactivateClientAccess') : t('activate')}
                    </button>
                    <button
                        type="button"
                        onClick={handleRefreshLink}
                        disabled={loadingAction}
                        className="px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm font-black rounded-lg"
                    >
                        {t('refreshAccessLink')}
                    </button>
                </div>

                {accessUrl && (
                    <div className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                        <span className="text-xs font-mono break-all flex-1 dir-ltr text-start">{accessUrl}</span>
                        <CopyButton text={accessUrl} copyLabel={t('copyLink')} copiedLabel={t('copied')} />
                    </div>
                )}

                <button
                    type="button"
                    onClick={handleOpenHousehold}
                    className="w-full py-3 bg-sky-600 text-white font-black rounded-xl"
                >
                    {t('openClientHousehold')}
                </button>

                {selected.client.userUid && (
                    <ConsultationThreadPanel
                        clientId={selected.clientId}
                        consultantId={selected.consultantId}
                        clientUid={selected.client.userUid}
                        householdId={selected.householdId}
                        language={currentUser.language}
                    />
                )}

                {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
        );
    };

    const renderClientList = () => (
        <div className="overflow-y-auto flex-1 min-h-0">
            {filteredRows.length === 0 ? (
                <p className="p-6 text-sm text-slate-500">{t('noGuidedClients')}</p>
            ) : (
                filteredRows.map((row) => {
                    const label = getClientLabel(row);
                    const unread = hasUnread(row);
                    const clientInitials = getInitials(label);
                    return (
                        <button
                            key={row.id}
                            type="button"
                            onClick={() => setSelectedAccessId(row.id)}
                            className={`w-full text-start p-4 border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 ${
                                selectedAccessId === row.id ? 'bg-sky-50 dark:bg-sky-900/20' : ''
                            }`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <AvatarImage
                                    src={getClientAvatarUrl(row.client)}
                                    alt={label}
                                    className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-slate-900 shadow-sm bg-brand-sage/15 shrink-0"
                                    fallback={
                                        <div className="w-10 h-10 rounded-full bg-brand-sage/15 text-brand-sage flex items-center justify-center font-black text-xs shrink-0 border-2 border-white dark:border-slate-900">
                                            {clientInitials}
                                        </div>
                                    }
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        {unread && (
                                            <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" title={t('guidedClientsUnreadBadge')} />
                                        )}
                                        <span className="font-bold text-slate-800 dark:text-slate-100 truncate">{label}</span>
                                    </div>
                                    {row.client && (
                                        <p className="text-xs text-slate-400 mt-1 truncate dir-ltr text-start">{row.client.email}</p>
                                    )}
                                    {isSuperuserView && (
                                        <p className="text-[10px] text-emerald-700 dark:text-emerald-300 mt-1 truncate">
                                            {t('assignedConsultant')}: {getConsultantLabel(row.consultantId)}
                                        </p>
                                    )}
                                    {unread && (
                                        <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 mt-1">
                                            {t('guidedClientsUnreadBadge')}
                                        </p>
                                    )}
                                </div>
                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded shrink-0 ${
                                    row.isActive
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                        : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                                }`}>
                                    {row.isActive ? t('active') : t('inactive')}
                                </span>
                            </div>
                        </button>
                    );
                })
            )}
        </div>
    );

    const showMobileDetail = selectedAccessId !== null;

    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-3 md:p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between gap-3 p-4 md:p-5 border-b border-slate-100 dark:border-slate-800 shrink-0">
                    {showMobileDetail ? (
                        <button
                            type="button"
                            onClick={() => setSelectedAccessId(null)}
                            className="md:hidden flex items-center gap-1 text-sm font-bold text-sky-600 dark:text-sky-400"
                        >
                            <ChevronDownIcon className="w-4 h-4 rotate-90" />
                            {t('backToClientList')}
                        </button>
                    ) : (
                        <div className="min-w-0">
                            <h2 className="text-lg font-black text-slate-800 dark:text-slate-100">{t('guidedClientsAdmin')}</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                                {isSuperuserView ? t('guidedClientsAdminAllHint') : t('guidedClientsAdminMineHint')}
                            </p>
                        </div>
                    )}
                    {showMobileDetail && selected && (
                        <div className="md:hidden min-w-0 flex-1 text-center px-2">
                            <p className="font-black text-slate-800 dark:text-slate-100 truncate">{getClientLabel(selected)}</p>
                        </div>
                    )}
                    <div className="flex items-center gap-2 shrink-0">
                        {isSuperuserView && onCreateClient && !showMobileDetail && (
                            <button
                                type="button"
                                onClick={onCreateClient}
                                className="px-3 py-2 bg-brand-sage text-white text-xs font-black rounded-lg"
                            >
                                {t('createGuidedClient')}
                            </button>
                        )}
                        <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
                            <XIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {!showMobileDetail && (
                    <>
                        <div className="px-4 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0 space-y-3">
                            <SearchField
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder={t('searchGuidedClients')}
                                clearLabel={t('clearSearch')}
                                inputClassName="py-2.5 rounded-xl"
                            />
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('active')}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide border ${
                                        statusFilter === 'active'
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                                            : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                    }`}
                                >
                                    {t('guidedClientsFilterActive')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('inactive')}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide border ${
                                        statusFilter === 'inactive'
                                            ? 'bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600'
                                            : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                    }`}
                                >
                                    {t('guidedClientsFilterInactive')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNotificationsOnly((prev) => !prev)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide border ${
                                        notificationsOnly
                                            ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
                                            : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                    }`}
                                >
                                    {t('guidedClientsFilterNotifications')}
                                    {unreadCount > 0 && (
                                        <span className="ms-1.5 inline-flex min-w-[1.1rem] h-[1.1rem] items-center justify-center rounded-full bg-amber-500 text-white text-[10px] px-1">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="md:hidden flex flex-col flex-1 min-h-0">
                            {renderClientList()}
                        </div>
                    </>
                )}

                {showMobileDetail && (
                    <div className="md:hidden flex flex-col flex-1 min-h-0">
                        {renderClientDetail()}
                    </div>
                )}

                <div className="hidden md:flex flex-1 min-h-0">
                    <div className="w-2/5 border-e border-slate-100 dark:border-slate-800 flex flex-col min-h-0">
                        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0 space-y-3">
                            <SearchField
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder={t('searchGuidedClients')}
                                clearLabel={t('clearSearch')}
                                inputClassName="py-2.5 rounded-xl"
                            />
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('active')}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide border ${
                                        statusFilter === 'active'
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                                            : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                    }`}
                                >
                                    {t('guidedClientsFilterActive')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('inactive')}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide border ${
                                        statusFilter === 'inactive'
                                            ? 'bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600'
                                            : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                    }`}
                                >
                                    {t('guidedClientsFilterInactive')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNotificationsOnly((prev) => !prev)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide border ${
                                        notificationsOnly
                                            ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
                                            : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                    }`}
                                >
                                    {t('guidedClientsFilterNotifications')}
                                    {unreadCount > 0 && (
                                        <span className="ms-1.5 inline-flex min-w-[1.1rem] h-[1.1rem] items-center justify-center rounded-full bg-amber-500 text-white text-[10px] px-1">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                        {renderClientList()}
                    </div>
                    <div className="w-3/5 flex flex-col min-h-0">
                        {renderClientDetail()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuidedClientManagementModal;
